import { Component, Input, OnInit } from '@angular/core';
import * as moment from 'moment';
import { FormBuilder, FormGroup } from '@angular/forms';

import { StandupDashboardServiceService } from '../standup-dashboard-service.service';
import {
  EscalateTaskToDashboardsGQL,
  TaskStatus,
  TaskType, UnEscalateTaskToDashboardsGQL,
  UpdateOperationsTaskMutationVariables
} from '../../../../generated/graphql';
import { TaskServiceService } from '../../../planner/task-service.service';
import { NotificationService } from '../../../shared/notification.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-promises-table',
  templateUrl: './promises-table.component.html',
  styleUrls: ['./promises-table.component.scss']
})
export class PromisesTableComponent implements OnInit {
  dateForm: FormGroup;

  categories: string[] = [
    'PROMISES_INTRO_CALL_INTERVIEW',
    'PROMISES_PRESENTED_TO_CLIENT',
    'PROMISES_CLIENT_INTERVIEW',
    'PROMISES_HEALTHCHECK',
    'PROMISES_CLIENT_EMPLOYED',
  ];

  categoriesFetch = 'PROMISES_INTRO_CALL_INTERVIEW,PROMISES_PRESENTED_TO_CLIENT,PROMISES_CLIENT_INTERVIEW,PROMISES_HEALTHCHECK,PROMISES_CLIENT_EMPLOYED';
  statsFetch = [];
  companyFetch = [];

  statsData = [];
  tableData: any[] = [];
  displayedColumns: string[] = [
    'actions', 'escalate', 'order', 'position', 'client', 'date', ...this.categories
  ];
  rawTableData: any[] = [];
  childDashboardUsers: string[] = [];
  escalatedNow = [];
  isEscalating = false;

  @Input() escalationDashboards = [];
  @Input() dashboardId: string;
  @Input() dashboardMembers: any;
  @Input() includeFromChildDashboards: boolean;
  @Input() dashboard: any;

  constructor(
    private dashboardService: StandupDashboardServiceService,
    private fb: FormBuilder,
    private tasksService: TaskServiceService,
    private escalateTaskGQL: EscalateTaskToDashboardsGQL,
    private unEscalateTaskGQL: UnEscalateTaskToDashboardsGQL,
    private notify: NotificationService,
    private translateService: TranslateService,
  ) { }

  ngOnInit(): void {
    this.dateForm = this.fb.group({
      dateFrom: [moment().startOf('isoWeek').toDate()]
    });
    this.dateForm.get('dateFrom').valueChanges.subscribe(date => {
      this.fetchData(date);
    });

    if (!this.dashboard.regionGroup) {
      const i = this.displayedColumns.indexOf('escalate');
      this.displayedColumns.splice(i, 1);
    }

    if (this.dashboard?.meetingLevel !== 'II') {
      const i = this.displayedColumns.indexOf('escalate');
      this.displayedColumns.splice(i, 1);
    }

    if (this.includeFromChildDashboards) {
      this.dashboardService.getUsersForCurrentChildDashboards(this.dashboardId).subscribe(resp => {
        if (resp.data.userprofiles.length) {
          this.childDashboardUsers = resp.data.userprofiles.map(profile => profile.id);
        }
        this.fetchData(this.dateForm.get('dateFrom').value);
      });
    } else {
      this.fetchData(this.dateForm.get('dateFrom').value);
    }
  }

  private fetchData(date) {
    this.dashboardService.getPromisesTasks({
      categoriesKeys: this.categoriesFetch,
      owners: this.childDashboardUsers.length ? this.childDashboardUsers : (this.dashboardMembers?.map(member => member.id) || []),
      date_Gte: moment(date).format('YYYY-MM-DD'),
      excludeStatuses: 'DONE'
    }).subscribe(result => {
      if (result.data.tasks.edges.length > 0) {
        const data = result.data.tasks.edges.map(edge => edge.node);
        this.setData(data);
        this.fetchStats(date);
      } else {
        this.tableData = [];
        this.rawTableData = [];
      }
    });
  }

  private fetchStats(date) {
    if (this.statsFetch.length > 0 || this.companyFetch.length > 0) {
      this.dashboardService.getOrderCandidateStats({
        orderIds: this.statsFetch,
        dateFrom: moment(date).format('YYYY-MM-DD'),
        companyIds: this.companyFetch
      }).subscribe(res => {
        this.statsData = res.data.candidateOrderStats.map(val => ({
          companyId: val.companyId,
          orderId: val.orderId,
          offeredFromDate: val.offeredFromDate,
          presentedClient: val.presentedClient,
          toBeInterviewedClient: val.toBeInterviewedClient,
          healthCheckPending: val.healthCheckPending,
          toBeEmployed: val.toBeEmployed
        }));

        this.tableData = this.rawTableData.map(v => {
          if (this.dashboard.regionGroup) {
            return {
              ...v,
              statsData: (v?.order !== null ? this.statsData.find(value => value.orderId === v.order?.id) :
                this.statsData.find(value => value.companyId === v?.company?.id)) || null,
              order: v?.order ? {
                ...v.order,
                id: atob(v.order.id).match(/\d+$/)?.[0] || ''
              } : null
            };
          }
          if (v?.taskEscalations?.edges.some(esc => esc.node.escalatedTo.id === this.dashboardId)) {
            return {
              ...v,
              statsData: (v?.order !== null ? this.statsData.find(value => value.orderId === v.order?.id) :
                this.statsData.find(value => value.companyId === v?.company?.id)) || null,
              order: v?.order ? {
                ...v.order,
                id: atob(v.order.id).match(/\d+$/)?.[0] || ''
              } : null
            };
          }
        }).filter(v => !!v);

        this.tableData.map(task => {
          task?.taskEscalations?.edges.map(item => {
            const targetDashboard = this.escalationDashboards?.find(d => d.meetingLevel === item.node?.escalatedTo?.meetingLevel);
            const input = {
              metricId: task?.id,
              level: item.node?.escalatedTo?.meetingLevel,
              dashboardId: targetDashboard?.id,
              escalationId: item?.node?.id,
              escalatedTo: item?.node?.escalatedTo?.id
            };
            this.escalatedNow.push(input);
          });
        });
      });
    }
  }

  private setData(data: any[]): void {
    this.rawTableData = [];
    data.forEach(value => {
      const orderId = value.order?.id;
      const categoryKey = value.category?.key;
      const comment = value.comment || '';

      value = {
        ...value,
        stats: { [categoryKey]: comment }
      };

      if (orderId) {
        const relatedTask = this.rawTableData.find(task => task.order?.id === orderId && task.date === value.date);

        if (relatedTask) {
          relatedTask.stats[categoryKey] = comment;
        } else {
          this.rawTableData.push(value);
        }
      } else {
        this.rawTableData.push(value);
      }
    });

    this.statsFetch = this.rawTableData.map(v => v.order?.id).filter(id => id);
    this.companyFetch = this.rawTableData.map(v => v.company?.id).filter(id => id);
  }

  onStatusChange($event: TaskStatus, task: any, updateImportance?: boolean) {
    if (updateImportance) {
      this.updateTask({ taskId: task.id, status: $event, isImportant: !task.isImportant}, task);
      task.isImportant = !task.isImportant;
    } else {
      this.updateTask({ taskId: task.id, status: $event }, task);
    }
  }

  updateTask(updatesTask: UpdateOperationsTaskMutationVariables, task?: TaskType) {
    this.tasksService.updateTaskPartial(updatesTask).subscribe(
      resp => {
        task.date = resp.data.updateOperationsTask.task.date;
        task.status = resp.data.updateOperationsTask.task.status;
        this.dashboardService.changedTaskSubject.next(resp.data.updateOperationsTask.task);
      }, () => { }
    );
  }

  escalateToLevel(task, level) {
    this.isEscalating = true;
    if (!this.isEscalatedToLevel(task, level)) {
      const targetDashboard = this.escalationDashboards?.find(d => d.meetingLevel === level && d.meetingLevel === 'II');

      if (!targetDashboard) {
        this.isEscalating = false;
        return;
      }

      if (this.isEscalatedToLevel(task, level)) {
        this.isEscalating = false;
        return;
      }

      this.escalateTaskGQL.mutate(
        {
          taskId: task.id,
          escalatedTo: [targetDashboard.id],
          escalatedFrom: this.dashboardId
        }
      ).subscribe(res => {
        if (res.data?.escalateTaskToDashboards?.task) {
          this.notify.notify(this.translateService.instant(
            'METRIC_SUCCESS_ESCALATION',
            { dashboard: targetDashboard.title || '' }
          ));
          task = {
            ...task,
            taskEscalations: {
              ...task.taskEscalations,
              edges: [...res.data?.escalateTaskToDashboards?.task.taskEscalations.edges]
            }
          };
          const input = {
            metricId: task.id,
            level,
            dashboardId: targetDashboard.id
          };
          res.data.escalateTaskToDashboards.task.taskEscalations.edges.map(v => {
            if (
              v.node.task.id === task.id &&
              v.node.escalatedTo.meetingLevel === level &&
              v.node.escalatedTo.id === targetDashboard.id
            ) {
              input['escalationId'] = v.node.id;
              input['escalatedTo'] = v.node.escalatedTo.id;
            }
          });
          this.escalatedNow.push(input);
          this.isEscalating = false;
        }
      }, e => this.isEscalating = false);
    } else {
      let targetId;
      const targetDashboard = this.escalationDashboards?.find(d => d.meetingLevel === level);
      this.escalatedNow.map(v => {
        if (task.id === v.metricId && v.level === level && v['escalatedTo'] === targetDashboard.id) {
          targetId = v['escalationId'];
        }
      });
      if (targetId) {
        this.unEscalateTaskGQL.mutate({escalation: targetId}).subscribe(val => {
          if (val.data.unEscalateTaskFromDashboards.ok) {
            this.escalatedNow = this.escalatedNow.filter(v =>
              v.metricId !== val.data.unEscalateTaskFromDashboards.task.id || v.level !== level);
            task = {
              ...task,
              taskEscalations: {
                ...task.taskEscalations,
                edges: task.taskEscalations.edges.filter(item => item.node.id !== targetId)
              }
            };
            this.isEscalating = false;
          }
          this.isEscalating = false;
        });
      }
    }
  }

  isEscalatedToLevel(element, level) {
    const targetDashboard = this.escalationDashboards?.find(d => d.meetingLevel === level);

    return this.escalatedNow.some(item =>
      item.metricId === element.id && item.level === level && item['escalatedTo'] === targetDashboard.id
    );
  }

  hasDashboardWithLevel(level: string): boolean {
    if (this.escalationDashboards?.length) {
      return this.escalationDashboards.some(d => d.meetingLevel === level);
    }
    return false;
  }

  getStatCellView(currentValue, target): string {
    if (target && !isNaN(Number(target))) {
      const goal = Number(target);

      if ((currentValue || 0) < goal) {
        return 'red';
      } else {
        return 'green-bold';
      }
    }

    return '';
  }
}
